import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Check, ChevronDown, Clock, Copy, EllipsisVertical, Eye, EyeOff, Webhook } from "lucide-react";
import { useSessions } from "@/modules/chat/hooks/use-sessions";
import { agentsCatalogueQueryOptions, type Agent } from "@/modules/chat/queries/agents-queries";
import { connectorsQueryOptions, type Connector } from "@/modules/chat/queries/connectors-queries";
import { MentionPopover } from "@/modules/chat/components/MentionPopover";
import { ConnectorIcon } from "@/shared/ui/ConnectorIcon";
import { useRoutineUi } from "./routine-store";
import {
  createRoutine,
  deleteRoutine,
  routineDetailQueryOptions,
  routineRunsQueryOptions,
  routinesQueryOptions,
  runRoutineNow,
  updateRoutine,
  type Routine,
  type RoutineInput,
  type RoutineKind,
  type RoutineRun,
} from "./routines-queries";
import {
  describeSchedule,
  presetIdForRoutine,
  routineSchedule,
  SCHEDULE_PRESETS,
  WEEKDAYS_RU,
} from "./schedule-utils";

/**
 * Scope shape:
 * - `personal` — routines pinned to the user's personal space; used by
 *   the sidebar /routines page. Agent / connector pickers also stay
 *   personal-only so the user never accidentally cross-pins a team
 *   resource into a personal routine.
 * - `{ kind: "space", id }` — routines inside a team space. Agent /
 *   connector pickers list that space's resources. Saved with `space_id`
 *   set to that uuid.
 */
export type RoutineScope = { kind: "personal" } | { kind: "space"; id: string };

export function RoutinesView({
  scope = { kind: "personal" },
}: {
  scope?: RoutineScope;
}) {
  const mode = useRoutineUi((s) => s.mode);
  switch (mode.kind) {
    case "detail":
      return <RoutineDetail key={mode.id} routineId={mode.id} scope={scope} />;
    case "create":
      return <RoutineForm key="new" scope={scope} />;
    case "list":
    default:
      return <RoutineList scope={scope} />;
  }
}

// ── List ──────────────────────────────────────────────────────────

function RoutineList({ scope }: { scope: RoutineScope }) {
  const ui = useRoutineUi();
  const { data: routines = [], isLoading } = useQuery(
    routinesQueryOptions(scope.kind === "personal" ? "personal" : "space"),
  );

  return (
    <div className="cmd-page">
      <header className="cmd-page-header">
        <h1 className="cmd-page-title">Автоматизации</h1>
        <button
          type="button"
          className="cmd-page-create"
          onClick={() => ui.openCreate()}
        >
          <PlusIcon />
          <span>Создать автоматизацию</span>
        </button>
      </header>

      <p className="cmd-builder-intro">
        Регулярные задачи запускаются выбранным агентом по расписанию или по внешнему событию.
      </p>

      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : routines.length === 0 ? (
        <div className="cmd-empty">
          <p className="cmd-empty-title">Пока нет автоматизаций</p>
          <p className="cmd-empty-hint">
            Создайте первую — например, утренний дайджест по почте.
          </p>
        </div>
      ) : (
        <div className="rt-list">
          {routines.map((r) => (
            <RoutineCard key={r.id} routine={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function RoutineCard({ routine }: { routine: Routine }) {
  const ui = useRoutineUi();
  const queryClient = useQueryClient();
  const { selectSession } = useSessions();
  const { data: catalogue } = useQuery(agentsCatalogueQueryOptions());
  const agent = useMemo(() => findAgent(catalogue, routine.agent_ref), [catalogue, routine.agent_ref]);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleEnabled = useMutation({
    mutationFn: () => updateRoutine(routine.id, { enabled: !routine.enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routines"] });
      queryClient.invalidateQueries({ queryKey: ["routines", routine.id] });
    },
  });

  const runNow = useMutation({
    mutationFn: () => runRoutineNow(routine.id),
    onSuccess: ({ session_id }) => {
      queryClient.invalidateQueries({ queryKey: ["routines", routine.id, "runs"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      selectSession(session_id);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteRoutine(routine.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["routines"] }),
  });

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const enabled = routine.enabled;
  const isWebhook = routine.schedule_kind === "webhook";

  return (
    <div
      className="rt-list-card"
      role="button"
      tabIndex={0}
      onClick={() => ui.openDetail(routine.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          ui.openDetail(routine.id);
        }
      }}
    >
      <span className="rt-list-card-icon" aria-hidden="true">
        {isWebhook ? <Webhook size={20} strokeWidth={1.75} /> : <CalendarDays size={20} strokeWidth={1.75} />}
      </span>

      <div className="rt-list-card-content">
        <div className="rt-list-card-title-row">
          <span className="rt-list-card-name" title={routine.name}>{routine.name}</span>
          <span className={`rt-list-card-badge rt-list-card-badge--${enabled ? "on" : "off"}`}>
            {enabled ? "Активно" : "Неактивно"}
          </span>
        </div>
        <div className="rt-list-card-meta">
          <span>{agent?.label || agent?.name || "Агент"}</span>
          <span className="rt-list-card-meta-dot">•</span>
          <span>{describeSchedule(routine)}</span>
        </div>
      </div>

      <div className="rt-list-card-actions" onClick={(e) => e.stopPropagation()}>
        <div className="rt-list-card-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="rt-list-card-kebab"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Меню"
          >
            <EllipsisVertical size={16} strokeWidth={1.75} />
          </button>
          {menuOpen && (
            <div className="rt-list-card-menu" role="menu">
              <button
                type="button"
                className="rt-list-card-menu-item"
                onClick={() => { setMenuOpen(false); ui.openEdit(routine.id); }}
              >
                Изменить
              </button>
              <button
                type="button"
                className="rt-list-card-menu-item"
                onClick={() => { setMenuOpen(false); runNow.mutate(); }}
                disabled={runNow.isPending}
              >
                Запустить сейчас
              </button>
              <button
                type="button"
                className="rt-list-card-menu-item"
                onClick={() => { setMenuOpen(false); toggleEnabled.mutate(); }}
                disabled={toggleEnabled.isPending}
              >
                {enabled ? "Выключить" : "Включить"}
              </button>
              <button
                type="button"
                className="rt-list-card-menu-item rt-list-card-menu-item--danger"
                onClick={() => {
                  setMenuOpen(false);
                  if (confirm("Удалить автоматизацию?")) remove.mutate();
                }}
                disabled={remove.isPending}
              >
                Удалить
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Detail (merged view + edit) ───────────────────────────────────
//
// Single editable page. No "Изменить" mode — the routine *is* its form.
// Status badge + run-now / toggle / delete live in the header. Save bar
// at the bottom appears only when the form is dirty.

function RoutineDetail({
  routineId,
  scope,
}: {
  routineId: string;
  scope: RoutineScope;
}) {
  const ui = useRoutineUi();
  const { selectSession } = useSessions();
  const queryClient = useQueryClient();
  const catalogueScope = scope.kind === "personal" ? "personal" : "space";

  const { data: routine } = useQuery(routineDetailQueryOptions(routineId));
  const { data: runs = [] } = useQuery(routineRunsQueryOptions(routineId));
  const { data: catalogue } = useQuery(agentsCatalogueQueryOptions(catalogueScope));
  const { data: connectors = [] } = useQuery(connectorsQueryOptions(catalogueScope));

  // ── Form state (hydrated once when routine + catalogue arrive) ──
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [agentRef, setAgentRef] = useState<string | null>(null);
  const [selectedConnectorIds, setSelectedConnectorIds] = useState<string[]>([]);
  const [scheduleKind, setScheduleKind] = useState<"time" | "webhook">("time");
  const [presetId, setPresetId] = useState<string>("weekdays");
  const [time, setTime] = useState("09:00");
  const [weekday, setWeekday] = useState(0);
  const [description, setDescription] = useState("");
  const [expectsFiles, setExpectsFiles] = useState(false);
  const [primed, setPrimed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Same logic as RoutineForm: chips never include the default agent.
  // It's the implicit fallback, not a pickable choice.
  const allAgents = useMemo<Agent[]>(() => {
    const defaultRef = catalogue?.defaultAgent?.ref;
    return (catalogue?.agents ?? []).filter((a) => a.ref !== defaultRef);
  }, [catalogue]);

  // Hydration: pull editable fields from `routine` once. Re-running this
  // when the user types would clobber their edits, hence the `primed` gate.
  const hydrate = (r: Routine) => {
    setName(r.name);
    setPrompt(r.prompt);
    setDescription(r.description || "");
    setExpectsFiles(r.expects_files);
    // If the saved routine points at the catalogue's default agent, treat
    // it as "no explicit pick" — the chip list will show no active state,
    // matching the create-form's empty default.
    const defaultRef = catalogue?.defaultAgent?.ref;
    const explicit = r.agent_ref === defaultRef ? null : r.agent_ref;
    setAgentRef(explicit);
    const agent = effectiveAgent(explicit, catalogue);
    const merged = [...(agent?.connector_ids ?? [])];
    for (const id of r.extra_connector_ids) if (!merged.includes(id)) merged.push(id);
    setSelectedConnectorIds(merged);
    const isWebhook = r.schedule_kind === "webhook";
    setScheduleKind(isWebhook ? "webhook" : "time");
    setPresetId(isWebhook ? "weekdays" : presetIdForRoutine(r));
    const sch = routineSchedule(r);
    if (sch.kind === "daily" || sch.kind === "weekdays" || sch.kind === "weekly") setTime(sch.time);
    if (sch.kind === "weekly") setWeekday(sch.weekday);
    setError(null);
  };

  useEffect(() => {
    if (primed || !routine) return;
    hydrate(routine);
    setPrimed(true);
    // hydrate closes over allAgents/catalogue; catalogue covers both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primed, routine, catalogue]);

  const pickAgent = (ref: string | null) => {
    if (!ref || ref === agentRef) {
      setAgentRef(null);
      setSelectedConnectorIds([...(catalogue?.defaultAgent?.connector_ids ?? [])]);
      return;
    }
    setAgentRef(ref);
    const agent = allAgents.find((a) => a.ref === ref);
    setSelectedConnectorIds([...(agent?.connector_ids ?? [])]);
  };

  const built = useMemo(() => {
    if (scheduleKind === "webhook") return { kind: "webhook" as const, spec: {} as Record<string, unknown> };
    const preset = SCHEDULE_PRESETS.find((p) => p.id === presetId) ?? SCHEDULE_PRESETS[0]!;
    return preset.build(time, weekday);
  }, [scheduleKind, presetId, time, weekday]);

  const dirty = useMemo(() => {
    if (!routine || !primed) return false;
    if (name.trim() !== routine.name) return true;
    if (prompt.trim() !== routine.prompt) return true;
    // Compare the effective ref so toggling chips back to "default" is not
    // flagged as dirty when the saved routine already pointed at default.
    const effective = effectiveAgent(agentRef, catalogue);
    if (effective?.ref !== routine.agent_ref) return true;
    if (description.trim() !== (routine.description || "")) return true;
    if (expectsFiles !== routine.expects_files) return true;
    if (built.kind !== routine.schedule_kind) return true;
    if (JSON.stringify(built.spec) !== JSON.stringify(routine.schedule_spec)) return true;
    const defaults = new Set(effective?.connector_ids ?? []);
    const extras = selectedConnectorIds.filter((id) => !defaults.has(id));
    const a = [...extras].sort();
    const b = [...routine.extra_connector_ids].sort();
    if (a.length !== b.length) return true;
    if (a.some((id, i) => id !== b[i])) return true;
    return false;
  }, [routine, primed, name, prompt, agentRef, catalogue, description, expectsFiles, built, selectedConnectorIds]);

  const save = useMutation({
    mutationFn: () => {
      const agent = effectiveAgent(agentRef, catalogue);
      if (!agent) throw new Error("Нет доступного агента");
      const defaults = new Set(agent.connector_ids ?? []);
      const extras = selectedConnectorIds.filter((id) => !defaults.has(id));
      const input: RoutineInput = {
        name: name.trim(),
        prompt: prompt.trim(),
        agent_ref: agent.ref,
        extra_connector_ids: extras,
        schedule_kind: built.kind as RoutineKind,
        schedule_spec: built.spec,
        enabled: routine?.enabled ?? true,
        description: description.trim(),
        prompt_template: null,
        expects_files: expectsFiles,
      };
      return updateRoutine(routineId, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routines"] });
      queryClient.invalidateQueries({ queryKey: ["routines", routineId] });
      setError(null);
    },
    onError: (e: Error) => setError(humanize(e.message)),
  });

  const toggleEnabled = useMutation({
    mutationFn: () =>
      updateRoutine(routineId, { enabled: !(routine?.enabled ?? true) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routines"] });
      queryClient.invalidateQueries({ queryKey: ["routines", routineId] });
    },
  });

  const runNow = useMutation({
    mutationFn: () => runRoutineNow(routineId),
    onSuccess: ({ session_id }) => {
      queryClient.invalidateQueries({ queryKey: ["routines", routineId, "runs"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      selectSession(session_id);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteRoutine(routineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routines"] });
      ui.openList();
    },
  });

  // Close kebab on outside click / Escape (agent picker manages its own).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  if (!routine) {
    return (
      <div className="cmd-builder">
        <BuilderTopbar title="Автоматизация" onBack={() => ui.openList()} />
        <div className="cmd-builder-body cmd-builder-body--narrow">
          <div className="cmd-empty">Загрузка…</div>
        </div>
      </div>
    );
  }

  const enabled = routine.enabled;

  return (
    <div className="cmd-builder">
      <BuilderTopbar
        title={routine.name || "Автоматизация"}
        onBack={() => ui.openList()}
        right={
          <>
            <button
              type="button"
              className="rt-status-toggle"
              onClick={() => toggleEnabled.mutate()}
              disabled={toggleEnabled.isPending}
              aria-pressed={enabled}
              title={enabled ? "Выключить автоматизацию" : "Включить автоматизацию"}
            >
              <span className={`rt-toggle${enabled ? " is-on" : ""}`} aria-hidden="true">
                <span className="rt-toggle-knob" />
              </span>
              <span className={`rt-list-card-badge rt-list-card-badge--${enabled ? "on" : "off"}`}>
                {enabled ? "Активно" : "Неактивно"}
              </span>
            </button>
            <div className="rt-list-card-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className="rt-list-card-kebab"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Меню"
              >
                <EllipsisVertical size={16} strokeWidth={1.75} />
              </button>
              {menuOpen && (
                <div className="rt-list-card-menu" role="menu">
                  <button
                    type="button"
                    className="rt-list-card-menu-item"
                    onClick={() => { setMenuOpen(false); runNow.mutate(); }}
                    disabled={runNow.isPending}
                  >
                    Запустить сейчас
                  </button>
                  <button
                    type="button"
                    className="rt-list-card-menu-item rt-list-card-menu-item--danger"
                    onClick={() => {
                      setMenuOpen(false);
                      if (confirm("Удалить автоматизацию?")) remove.mutate();
                    }}
                    disabled={remove.isPending}
                  >
                    Удалить
                  </button>
                </div>
              )}
            </div>
          </>
        }
      />

      <div className="cmd-builder-body cmd-builder-body--split">
        <div className="rt-detail-layout">
        <div className="rt-detail-form">
        <div className="cmd-builder-fields">
          <Field label="Название">
            <input
              type="text"
              className="cmd-builder-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Утренний дайджест почты"
              maxLength={200}
              autoComplete="off"
            />
          </Field>

          <Field label="Запускать">
            <RoutineScheduleEditor
              scheduleKind={scheduleKind}
              presetId={presetId}
              time={time}
              weekday={weekday}
              onScheduleKindChange={setScheduleKind}
              onPresetIdChange={setPresetId}
              onTimeChange={setTime}
              onWeekdayChange={setWeekday}
            />
          </Field>

          <Field label="Задача">
            <RoutinePromptCard
              prompt={prompt}
              onPromptChange={setPrompt}
              agents={allAgents}
              agentRef={agentRef}
              onAgentChange={pickAgent}
              connectors={connectors}
              selectedConnectorIds={selectedConnectorIds}
              onConnectorsChange={setSelectedConnectorIds}
            />
            {scheduleKind === "webhook" && <TriggerPlaceholdersHint />}
          </Field>

        </div>

        {scheduleKind === "webhook" && routine.webhook_token && (
          <PublicApiBlock
            routine={routine}
            description={description}
            onDescriptionChange={setDescription}
            expectsFiles={expectsFiles}
            onExpectsFilesChange={setExpectsFiles}
          />
        )}
        </div>

        <aside className="rt-detail-side">
          <section className="rt-section-block">
            <div className="rt-side-head">
              <span className="rt-section-label">Запуски</span>
              {runs.length > 0 && (
                <span className="rt-side-count">{runs.length}</span>
              )}
            </div>
            {runs.length === 0 ? (
              <div className="rt-side-empty">
                Запусков ещё не было. Запустите вручную или дождитесь расписания.
              </div>
            ) : (
              <div className="rt-runs-list">
                {groupRunsByDate(runs).map((group) => (
                  <div key={group.key} className="rt-runs-group">
                    <div className="rt-runs-group-head">{group.label}</div>
                    {group.runs.map((run) => (
                      <RunRow
                        key={run.session_id}
                        run={run}
                        onOpen={() => selectSession(run.session_id)}
                        showOnlyTime={group.bucket === "today" || group.bucket === "yesterday"}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </section>
        </aside>
        </div>
      </div>

      {(dirty || save.isPending || error) && (
        <div className="rt-save-bar">
          {error && <span className="rt-save-bar-error">{error}</span>}
          <div className="rt-save-bar-spacer" />
          <button
            type="button"
            className="cmd-builder-cancel"
            onClick={() => hydrate(routine)}
            disabled={save.isPending}
          >
            Отменить
          </button>
          <button
            type="button"
            className="cmd-page-create"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      )}
    </div>
  );
}

function PublicApiBlock({
  routine,
  description,
  onDescriptionChange,
  expectsFiles,
  onExpectsFilesChange,
}: {
  routine: Routine;
  description: string;
  onDescriptionChange: (v: string) => void;
  /** Live form value — preview the snippet for the unsaved toggle state. */
  expectsFiles: boolean;
  onExpectsFilesChange: (v: boolean) => void;
}) {
  // The browser hosts the SPA on the same origin as the API in dev (proxy)
  // and prod (single nginx). Showing window.location.origin keeps the URL
  // honest for whoever copy-pastes it from this card.
  const url = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/api/triggers/${routine.webhook_token}`;
  }, [routine.webhook_token]);

  const maskedUrl = useMemo(() => maskTokenInUrl(url), [url]);
  const [revealed, setRevealed] = useState(false);
  const displayUrl = revealed ? url : maskedUrl;

  const [lang, setLang] = useState<ApiLanguage>(() => readLangPref());
  useEffect(() => {
    try {
      window.localStorage.setItem(API_LANG_STORAGE_KEY, lang);
    } catch {
      // localStorage may be disabled (private mode, quota); harmless.
    }
  }, [lang]);

  // Two snippets: one for display (uses masked URL when not revealed),
  // one for clipboard (always real). Keeps the visible code honest about
  // what the dashboard exposes vs. what gets pasted.
  const displaySnippet = useMemo(
    () => buildApiSnippet(lang, displayUrl, expectsFiles),
    [lang, displayUrl, expectsFiles],
  );
  const copySnippet = useMemo(
    () => buildApiSnippet(lang, url, expectsFiles),
    [lang, url, expectsFiles],
  );

  return (
    <section className="rt-section-block">
      <div className="rt-section-label">Public API</div>
      <div className="rt-public-api">
        <div className="rt-public-api-url-row">
          <CopyableSurface value={url} className="rt-public-api-url">
            {displayUrl}
          </CopyableSurface>
          <button
            type="button"
            className="rt-url-reveal"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? "Скрыть токен" : "Показать токен"}
            title={revealed ? "Скрыть токен" : "Показать токен"}
          >
            {revealed ? <EyeOff size={14} strokeWidth={1.75} /> : <Eye size={14} strokeWidth={1.75} />}
          </button>
        </div>
        <div className="cmd-builder-field-hint">
          URL — это ключ доступа. Кому он известен — может запускать рутину. Не публикуйте.
        </div>

        <Field label="Описание для внешней системы">
          <input
            type="text"
            className="cmd-builder-input"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Короткая публичная справка — что делает эта приёмная"
            maxLength={200}
            spellCheck
          />
          <div className="cmd-builder-field-hint">
            Видно через GET <code>/api/triggers/{"{token}"}</code>.
          </div>
        </Field>

        <label className="rt-toggle-field">
          <span className="cmd-builder-field-label">Принимает файлы (multipart)</span>
          <span className={`rt-toggle${expectsFiles ? " is-on" : ""}`} aria-hidden="true">
            <span className="rt-toggle-knob" />
          </span>
          <input
            type="checkbox"
            className="rt-toggle-input"
            checked={expectsFiles}
            onChange={(e) => onExpectsFilesChange(e.target.checked)}
          />
        </label>

        <div className="rt-api-example">
          <div className="rt-section-sublabel">Пример запроса</div>
          <div className="rt-api-tabs" role="tablist">
            {API_LANGUAGES.map((l) => (
              <button
                key={l.id}
                type="button"
                role="tab"
                aria-selected={lang === l.id}
                className={`rt-api-tab${lang === l.id ? " is-active" : ""}`}
                onClick={() => setLang(l.id)}
              >
                {l.label}
              </button>
            ))}
          </div>
          <CopyableSurface value={copySnippet} className="rt-snippet-surface">
            <pre className="rt-public-api-curl">{displaySnippet}</pre>
          </CopyableSurface>
        </div>
      </div>
    </section>
  );
}

/** Click-anywhere-to-copy wrapper. Renders children in a positioned div
 *  with a hover-revealed copy icon top-right and a brief "Скопировано"
 *  flash on success. Keyboard-accessible via Enter/Space. */
function CopyableSurface({
  value,
  children,
  className = "",
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const fire = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <div
      className={`copy-surface${className ? " " + className : ""}${copied ? " is-copied" : ""}`}
      role="button"
      tabIndex={0}
      onClick={fire}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          fire();
        }
      }}
      aria-label="Скопировать"
      title="Нажмите, чтобы скопировать"
    >
      {children}
      {copied && <span className="copy-surface-flash" aria-hidden="true">Скопировано</span>}
      <span className="copy-surface-icon" aria-hidden="true">
        {copied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={1.75} />}
      </span>
    </div>
  );
}

/** Mask the token segment of `.../api/triggers/<TOKEN>`. Show first 3 +
 *  last 3 chars with bullets in between, so the user can recognize their
 *  own URL while not exposing the secret on screen. */
function maskTokenInUrl(url: string): string {
  const idx = url.lastIndexOf("/");
  if (idx === -1) return url;
  const token = url.slice(idx + 1);
  if (!token) return url;
  if (token.length <= 8) return url.slice(0, idx + 1) + "•".repeat(token.length);
  return (
    url.slice(0, idx + 1) +
    token.slice(0, 3) +
    "•".repeat(Math.max(8, token.length - 6)) +
    token.slice(-3)
  );
}

type ApiLanguage = "curl" | "python" | "js";

const API_LANGUAGES: Array<{ id: ApiLanguage; label: string }> = [
  { id: "curl", label: "curl" },
  { id: "python", label: "Python" },
  { id: "js", label: "JavaScript" },
];

const API_LANG_STORAGE_KEY = "routines.api_language";

function readLangPref(): ApiLanguage {
  try {
    const v = window.localStorage.getItem(API_LANG_STORAGE_KEY);
    if (v === "curl" || v === "python" || v === "js") return v;
  } catch {
    // ignore — fall through to default
  }
  return "curl";
}

function buildApiSnippet(
  lang: ApiLanguage,
  url: string,
  expectsFiles: boolean,
): string {
  if (lang === "curl") {
    if (expectsFiles) {
      return [
        `curl -X POST "${url}" \\`,
        `  -H "Idempotency-Key: $(uuidgen)" \\`,
        `  -F 'comment=обработай присланные файлы' \\`,
        `  -F 'file=@./document.pdf'`,
      ].join("\n");
    }
    return [
      `curl -X POST "${url}" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -H "Idempotency-Key: $(uuidgen)" \\`,
      `  -d '{"comment":"...","payload":{}}'`,
    ].join("\n");
  }

  if (lang === "python") {
    if (expectsFiles) {
      return [
        `import requests, uuid`,
        ``,
        `with open("document.pdf", "rb") as f:`,
        `    requests.post(`,
        `        "${url}",`,
        `        headers={"Idempotency-Key": str(uuid.uuid4())},`,
        `        data={"comment": "обработай присланные файлы"},`,
        `        files={"file": f},`,
        `    )`,
      ].join("\n");
    }
    return [
      `import requests, uuid`,
      ``,
      `requests.post(`,
      `    "${url}",`,
      `    headers={"Idempotency-Key": str(uuid.uuid4())},`,
      `    json={"comment": "...", "payload": {}},`,
      `)`,
    ].join("\n");
  }

  // js
  if (expectsFiles) {
    return [
      `const form = new FormData();`,
      `form.append("comment", "обработай присланные файлы");`,
      `form.append("file", fileBlob, "document.pdf");`,
      ``,
      `await fetch("${url}", {`,
      `  method: "POST",`,
      `  headers: { "Idempotency-Key": crypto.randomUUID() },`,
      `  body: form,`,
      `});`,
    ].join("\n");
  }
  return [
    `await fetch("${url}", {`,
    `  method: "POST",`,
    `  headers: {`,
    `    "Content-Type": "application/json",`,
    `    "Idempotency-Key": crypto.randomUUID(),`,
    `  },`,
    `  body: JSON.stringify({ comment: "...", payload: {} }),`,
    `});`,
  ].join("\n");
}

function RunRow({
  run,
  onOpen,
  showOnlyTime,
}: {
  run: RoutineRun;
  onOpen: () => void;
  /** True for Сегодня/Вчера groups — the date already lives in the header. */
  showOnlyTime: boolean;
}) {
  const time = run.created_at
    ? new Date(run.created_at).toLocaleString(
        "ru-RU",
        showOnlyTime
          ? { hour: "2-digit", minute: "2-digit" }
          : { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" },
      )
    : "—";
  return (
    <button type="button" className="rt-run-row" onClick={onOpen}>
      <span className="rt-run-title">{run.title || "Запуск"}</span>
      <span className="rt-run-time">{time}</span>
    </button>
  );
}

/** Bucket runs into Сегодня / Вчера / Предыдущие 7 дней / Предыдущие 30 дней,
 *  then per-month for older ones. Assumes input is already newest-first. */
function groupRunsByDate(runs: RoutineRun[]): Array<{
  key: string;
  label: string;
  bucket: "today" | "yesterday" | "week" | "month" | "older" | "unknown";
  runs: RoutineRun[];
}> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 86400000;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  type Group = { key: string; label: string; bucket: "today" | "yesterday" | "week" | "month" | "older" | "unknown"; runs: RoutineRun[] };
  const map = new Map<string, Group>();
  const pushTo = (key: string, label: string, bucket: Group["bucket"], run: RoutineRun) => {
    let g = map.get(key);
    if (!g) { g = { key, label, bucket, runs: [] }; map.set(key, g); }
    g.runs.push(run);
  };

  for (const run of runs) {
    if (!run.created_at) {
      pushTo("unknown", "Без даты", "unknown", run);
      continue;
    }
    const d = new Date(run.created_at);
    const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diff = Math.floor((today - dStart) / day);

    if (diff === 0) pushTo("today", "Сегодня", "today", run);
    else if (diff === 1) pushTo("yesterday", "Вчера", "yesterday", run);
    else if (diff <= 7) pushTo("week", "Предыдущие 7 дней", "week", run);
    else if (diff <= 30) pushTo("month", "Предыдущие 30 дней", "month", run);
    else {
      const key = `m-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const monthName = cap(new Intl.DateTimeFormat("ru-RU", { month: "long" }).format(d));
      const label = d.getFullYear() === now.getFullYear() ? monthName : `${monthName} ${d.getFullYear()}`;
      pushTo(key, label, "older", run);
    }
  }
  return Array.from(map.values());
}

// ── Form (create / edit) ──────────────────────────────────────────

function RoutineForm({ scope }: { scope: RoutineScope }) {
  const ui = useRoutineUi();
  const queryClient = useQueryClient();

  const catalogueScope = scope.kind === "personal" ? "personal" : "space";
  const { data: catalogue } = useQuery(agentsCatalogueQueryOptions(catalogueScope));
  const { data: connectors = [] } = useQuery(connectorsQueryOptions(catalogueScope));

  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [agentRef, setAgentRef] = useState<string | null>(null);
  // Effective full connector list this routine should run with. We expose
  // it directly to the UI (mirrors the hero pill — one set of selected
  // connectors, no "extras" abstraction) and split it into agent-defaults
  // vs extras only at save time.
  const [selectedConnectorIds, setSelectedConnectorIds] = useState<string[]>([]);
  const [scheduleKind, setScheduleKind] = useState<"time" | "webhook">("time");
  const [presetId, setPresetId] = useState<string>("weekdays");
  const [time, setTime] = useState("09:00");
  const [weekday, setWeekday] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [primed, setPrimed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Public webhook contract (EXTERNAL_API.md §3) — only meaningful for
  // schedule_kind=webhook, but we keep the values around so a kind switch
  // doesn't blow them away.
  const [description, setDescription] = useState("");
  const [expectsFiles, setExpectsFiles] = useState(false);

  // Chips show only user-visible agents — the catalogue's defaultAgent is
  // the implicit fallback when nothing is picked, so it must not appear as
  // a pickable chip (the user would otherwise "select" the same agent that
  // the empty state already represents).
  const allAgents: Agent[] = useMemo(() => {
    const defaultRef = catalogue?.defaultAgent?.ref;
    return (catalogue?.agents ?? []).filter((a) => a.ref !== defaultRef);
  }, [catalogue]);

  // Prefill connectors with the default agent's defaults so the connectors
  // pill reflects what will actually run if the user leaves agentRef empty.
  // We do NOT prefill agentRef itself — empty = "use the default agent".
  useEffect(() => {
    if (primed) return;
    const baseline = catalogue?.defaultAgent?.connector_ids ?? [];
    setSelectedConnectorIds([...baseline]);
    setPrimed(true);
  }, [primed, catalogue]);

  // Picking a chip swaps connectors to that agent's defaults. Clicking the
  // already-active chip toggles back to "no explicit pick" (default agent),
  // restoring the default's connectors as the baseline.
  const pickAgent = (ref: string | null) => {
    if (!ref || ref === agentRef) {
      setAgentRef(null);
      setSelectedConnectorIds([...(catalogue?.defaultAgent?.connector_ids ?? [])]);
      return;
    }
    setAgentRef(ref);
    const agent = allAgents.find((a) => a.ref === ref);
    setSelectedConnectorIds([...(agent?.connector_ids ?? [])]);
  };

  const built = useMemo(() => {
    if (scheduleKind === "webhook") return { kind: "webhook" as const, spec: {} as Record<string, unknown> };
    const preset = SCHEDULE_PRESETS.find((p) => p.id === presetId) ?? SCHEDULE_PRESETS[0]!;
    return preset.build(time, weekday);
  }, [scheduleKind, presetId, time, weekday]);

  const save = useMutation({
    mutationFn: async () => {
      // Resolve the effective agent: explicit pick → that one; otherwise
      // catalogue's default agent. Backend's agent_ref is required, so we
      // never send an empty value — implicit "use default" is encoded as
      // sending the default's ref explicitly.
      const agent = effectiveAgent(agentRef, catalogue);
      if (!agent) throw new Error("Нет доступного агента");
      // Split effective set into agent-defaults vs extras. Backend stores
      // only the extras and re-applies the agent's defaults at fire time.
      const defaults = new Set(agent.connector_ids ?? []);
      const extras = selectedConnectorIds.filter((id) => !defaults.has(id));
      const input: RoutineInput = {
        name: name.trim(),
        prompt: prompt.trim(),
        agent_ref: agent.ref,
        extra_connector_ids: extras,
        schedule_kind: built.kind as RoutineKind,
        schedule_spec: built.spec,
        enabled,
        description: description.trim(),
        prompt_template: null,
        expects_files: expectsFiles,
        space_id: scope.kind === "space" ? scope.id : null,
      };
      return createRoutine(input);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["routines"] });
      ui.openDetail(created.id);
    },
    onError: (e: Error) => setError(humanize(e.message)),
  });

  const canSave =
    name.trim().length > 0 &&
    prompt.trim().length > 0 &&
    !save.isPending;

  function handleSave() {
    setError(null);
    if (!name.trim()) {
      setError("Название не может быть пустым.");
      return;
    }
    if (!prompt.trim()) {
      setError("Опишите, что должна делать автоматизация.");
      return;
    }
    save.mutate();
  }

  const title = "Планирование задачи";

  return (
    <div className="cmd-builder">
      <BuilderTopbar title={title} onBack={() => ui.openList()} />
      <div className="cmd-builder-body cmd-builder-body--narrow">
        {error && <div className="cmd-builder-error">{error}</div>}

        <div className="cmd-builder-fields">
          <Field label="Название">
            <input
              type="text"
              className="cmd-builder-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Название задачи"
              maxLength={200}
              autoComplete="off"
              autoFocus
            />
          </Field>

          <Field label="Запускать">
            <RoutineScheduleEditor
              scheduleKind={scheduleKind}
              presetId={presetId}
              time={time}
              weekday={weekday}
              onScheduleKindChange={setScheduleKind}
              onPresetIdChange={setPresetId}
              onTimeChange={setTime}
              onWeekdayChange={setWeekday}
            />
          </Field>

          <Field label="Задача">
            <RoutinePromptCard
              prompt={prompt}
              onPromptChange={setPrompt}
              agents={allAgents}
              agentRef={agentRef}
              onAgentChange={pickAgent}
              connectors={connectors}
              selectedConnectorIds={selectedConnectorIds}
              onConnectorsChange={setSelectedConnectorIds}
            />
            {scheduleKind === "webhook" && <TriggerPlaceholdersHint />}
          </Field>

          {built.kind === "webhook" && (
            <>
              <Field label="Описание для внешней системы">
                <input
                  type="text"
                  className="cmd-builder-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Короткая публичная справка — что делает эта приёмная"
                  maxLength={200}
                  spellCheck
                />
                <div className="cmd-builder-field-hint">
                  Видно через GET <code>/api/triggers/{"{token}"}</code>.
                </div>
              </Field>

              <label className="rt-toggle-field">
                <span className="cmd-builder-field-label">Принимает файлы (multipart)</span>
                <span className={`rt-toggle${expectsFiles ? " is-on" : ""}`} aria-hidden="true">
                  <span className="rt-toggle-knob" />
                </span>
                <input
                  type="checkbox"
                  className="rt-toggle-input"
                  checked={expectsFiles}
                  onChange={(e) => setExpectsFiles(e.target.checked)}
                />
              </label>
            </>
          )}

          <label className="rt-toggle-field">
            <span className="cmd-builder-field-label">Активна</span>
            <span className={`rt-toggle${enabled ? " is-on" : ""}`} aria-hidden="true">
              <span className="rt-toggle-knob" />
            </span>
            <input
              type="checkbox"
              className="rt-toggle-input"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </label>
        </div>
      </div>

      <div className="rt-save-bar">
        <div className="rt-save-bar-spacer" />
        <button
          type="button"
          className="cmd-builder-cancel"
          onClick={() => ui.openList()}
          disabled={save.isPending}
        >
          Отмена
        </button>
        <button
          type="button"
          className="cmd-page-create"
          onClick={handleSave}
          disabled={!canSave}
        >
          {save.isPending ? "Создаём…" : "Запланировать"}
        </button>
      </div>
    </div>
  );
}

// ── Pickers / inputs ──────────────────────────────────────────────

/** Hero-input-style card for the "Задача" field. Visually mirrors the
 *  chat composer (HeroBlock + InputBar): rounded #fafafa shell with a
 *  white inner block holding the textarea and a left toolbar carrying
 *  the @-mention agent picker, plus a foot row with the connectors pill
 *  aligned to the right. */
function RoutinePromptCard({
  prompt,
  onPromptChange,
  agents,
  agentRef,
  onAgentChange,
  connectors,
  selectedConnectorIds,
  onConnectorsChange,
}: {
  prompt: string;
  onPromptChange: (v: string) => void;
  agents: Agent[];
  agentRef: string | null;
  onAgentChange: (ref: string | null) => void;
  connectors: Connector[];
  selectedConnectorIds: string[];
  onConnectorsChange: (ids: string[]) => void;
}) {
  return (
    <div className="hero-input-card rt-task-card">
      <div className="hero-input-inner">
        <div className="hero-input-row">
          <textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="Что нужно сделать?"
            rows={3}
            spellCheck
            className="hero-input-textarea rt-task-textarea"
          />
        </div>
        <div className="hero-input-bar">
          <div className="hero-bar-left">
            <RoutineAgentMentionPicker
              agents={agents}
              value={agentRef}
              onChange={onAgentChange}
            />
          </div>
        </div>
      </div>
      <div className="hero-input-foot rt-task-foot">
        <div className="rt-task-foot-spacer" />
        <ConnectorsPicker
          all={connectors}
          selected={selectedConnectorIds}
          onChange={onConnectorsChange}
        />
      </div>
    </div>
  );
}

/** Agent picker styled as the chat hero's @-button:
 *  - no pick → small @-icon button
 *  - picked → "Агент: <name> ×" pill, click swaps, × clears
 *  Click on either trigger opens a dropdown listing the agents (excluding
 *  the implicit default — caller passes the filtered list). */
function RoutineAgentMentionPicker({
  agents,
  value,
  onChange,
}: {
  agents: Agent[];
  value: string | null;
  onChange: (ref: string | null) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const current = value ? agents.find((a) => a.ref === value) ?? null : null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const clear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onChange(null);
    setOpen(false);
  };

  return (
    <div className="hero-agent-pill-wrap rt-agent-mention-wrap" ref={wrapRef}>
      {current ? (
        <button
          type="button"
          className="hero-agent-pill"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="hero-agent-pill-prefix">Агент:</span>
          <span className="hero-agent-pill-name">{current.label || current.name}</span>
          <span
            role="button"
            tabIndex={0}
            className="hero-agent-pill-clear"
            aria-label="Сбросить агента"
            title="Сбросить агента"
            onClick={clear}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                clear(e);
              }
            }}
          >
            <CloseIcon />
          </span>
        </button>
      ) : (
        <button
          type="button"
          className="hero-agent-at-btn"
          onClick={() => setOpen((v) => !v)}
          title="Выбрать агента"
          aria-label="Выбрать агента"
        >
          <AtIcon />
        </button>
      )}
      {open && agents.length > 0 && (
        <MentionPopover
          open
          placement="below"
          width="intrinsic"
          items={agents.map((a) => ({
            id: a.ref,
            label: a.label || a.name,
            description: a.description || undefined,
          }))}
          currentId={value}
          onPick={(ref) => { onChange(ref); setOpen(false); }}
        />
      )}
    </div>
  );
}

/** Schedule editor: segmented "По времени"/"По триггеру" toggle plus the
 *  nested preset/weekday/time row when a time-based schedule is picked.
 *  Webhook-specific fields (description/template/expects-files) live in
 *  the parent — only the kind toggle is here. */
function RoutineScheduleEditor({
  scheduleKind,
  presetId,
  time,
  weekday,
  onScheduleKindChange,
  onPresetIdChange,
  onTimeChange,
  onWeekdayChange,
}: {
  scheduleKind: "time" | "webhook";
  presetId: string;
  time: string;
  weekday: number;
  onScheduleKindChange: (kind: "time" | "webhook") => void;
  onPresetIdChange: (id: string) => void;
  onTimeChange: (t: string) => void;
  onWeekdayChange: (w: number) => void;
}) {
  const timePresets = SCHEDULE_PRESETS.filter((p) => p.id !== "webhook");
  const currentPreset = timePresets.find((p) => p.id === presetId) ?? timePresets[0]!;

  return (
    <div className="rt-schedule">
      <div className="rt-schedule-segmented" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={scheduleKind === "time"}
          className={`rt-schedule-seg${scheduleKind === "time" ? " is-active" : ""}`}
          onClick={() => onScheduleKindChange("time")}
        >
          <Clock size={16} strokeWidth={1.75} />
          <span>По времени</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={scheduleKind === "webhook"}
          className={`rt-schedule-seg${scheduleKind === "webhook" ? " is-active" : ""}`}
          onClick={() => onScheduleKindChange("webhook")}
        >
          <Webhook size={16} strokeWidth={1.75} />
          <span>По триггеру</span>
        </button>
      </div>

      {scheduleKind === "time" ? (
        <div className="rt-schedule-controls">
          <SchedulePresetSelect
            presets={timePresets}
            value={presetId}
            onChange={onPresetIdChange}
          />
          {currentPreset.needsWeekday && (
            <div className="rt-weekday-chips" role="radiogroup" aria-label="День недели">
              {WEEKDAYS_RU.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={weekday === d.id}
                  className={`rt-weekday-chip${weekday === d.id ? " is-active" : ""}`}
                  onClick={() => onWeekdayChange(d.id)}
                >
                  {d.short.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          {currentPreset.needsTime && (
            <>
              <span className="rt-schedule-prefix">в</span>
              <input
                type="time"
                className="rt-schedule-select rt-schedule-select--time"
                value={time}
                onChange={(e) => onTimeChange(e.target.value)}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Custom-styled trigger over the schedule preset list. The native &lt;select&gt;
 *  bled the OS chevron / option styling; this routes through MentionPopover
 *  so the dropdown matches every other list popover in the app. */
function SchedulePresetSelect({
  presets,
  value,
  onChange,
}: {
  presets: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const current = presets.find((p) => p.id === value) ?? presets[0]!;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="rt-schedule-pickwrap" ref={wrapRef}>
      <button
        type="button"
        className="rt-schedule-select rt-schedule-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{current.label}</span>
        <ChevronDown size={14} strokeWidth={1.75} />
      </button>
      {open && (
        <MentionPopover
          open
          placement="below"
          width="intrinsic"
          items={presets.map((p) => ({ id: p.id, label: p.label }))}
          currentId={value}
          onPick={(id) => { onChange(id); setOpen(false); }}
        />
      )}
    </div>
  );
}

const MAX_CONN_AVATARS = 3;

function ConnectorsPicker({
  all,
  selected,
  onChange,
}: {
  all: Connector[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const enabled = all.filter((c) => c.enabled);
  const selectedItems = useMemo(
    () =>
      selected
        .map((id) => enabled.find((c) => c.id === id))
        .filter((c): c is Connector => !!c),
    [selected, enabled],
  );

  // Anchor popover via portal so it escapes the cmd-builder body's overflow.
  // Flip above when there isn't room below — matches the chat input pill.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const trig = triggerRef.current?.getBoundingClientRect();
      if (!trig) return;
      const popH = popoverRef.current?.getBoundingClientRect().height ?? 320;
      const margin = 8;
      const spaceBelow = window.innerHeight - trig.bottom;
      const flipUp = spaceBelow < popH + margin && trig.top > spaceBelow;
      const top = flipUp
        ? Math.max(margin, trig.top - popH - margin)
        : trig.bottom + margin;
      const popW = popoverRef.current?.getBoundingClientRect().width ?? 280;
      const left = Math.min(
        Math.max(margin, trig.left),
        window.innerWidth - popW - margin,
      );
      setPos({ top, left });
    };
    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  // Trigger label rules mirror the hero pill: 0 → "Подключить коннектор"; 1..3 → only avatars; 4+ → 3 avatars + "+ N коннекторов".
  const total = selectedItems.length;
  const showIcons = total > 0;
  const extras = Math.max(0, total - MAX_CONN_AVATARS);
  const countText = (() => {
    if (total === 0) return "Подключить коннектор";
    if (extras > 0) return `+ ${extras} коннектор${plural(extras)}`;
    return "";
  })();

  return (
    <div className="rt-conn-picker" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="hero-conn-pill rt-conn-pill"
        onClick={() => setOpen((v) => !v)}
      >
        {showIcons && (
          <span className="hero-conn-stack" aria-hidden="true">
            {selectedItems.slice(0, MAX_CONN_AVATARS).map((c) => (
              <span key={c.id} className="hero-conn-avatar-wrap" title={c.label || c.name}>
                <ConnectorIcon
                  url={(c.config?.url as string | undefined) ?? null}
                  label={c.label || c.name}
                  size={24}
                />
              </span>
            ))}
          </span>
        )}
        {countText && <span>{countText}</span>}
        <Chevron />
      </button>

      {open && pos && createPortal(
        <div
          ref={popoverRef}
          className="hero-conn-popover"
          role="menu"
          style={{ top: pos.top, left: pos.left }}
        >
          {enabled.length === 0 ? (
            <div className="hero-conn-popover-empty">
              В пространстве пока нет коннекторов.
            </div>
          ) : (
            <ul className="hero-conn-popover-list">
              {enabled.map((c) => {
                const isOn = selected.includes(c.id);
                return (
                  <li key={c.id}>
                    <label className={`hero-conn-popover-item${isOn ? " is-active" : ""}`}>
                      <span className="hero-conn-popover-icon">
                        <ConnectorIcon
                          url={(c.config?.url as string | undefined) ?? null}
                          label={c.label || c.name}
                          size={20}
                        />
                      </span>
                      <span className="hero-conn-popover-name">{c.label || c.name}</span>
                      <span className={`hero-conn-toggle${isOn ? " is-on" : ""}`} aria-hidden="true">
                        <span className="hero-conn-toggle-knob" />
                      </span>
                      <input
                        type="checkbox"
                        className="hero-conn-toggle-input"
                        checked={isOn}
                        onChange={() => toggle(c.id)}
                      />
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Inline hint shown under «Задача» when the trigger mode is active.
 *  Tells the user they can interpolate fields from the inbound POST. */
function TriggerPlaceholdersHint() {
  return (
    <div className="cmd-builder-field-hint rt-task-hint">
      Можно подставить данные из запроса:{" "}
      <code>{"{comment}"}</code>, <code>{"{file_list}"}</code>,{" "}
      <code>{"{payload.<ключ>}"}</code>.
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="cmd-builder-field">
      <span className="cmd-builder-field-label">{label}</span>
      {children}
    </label>
  );
}

function BuilderTopbar({
  title,
  onBack,
  badge,
  right,
}: {
  title: string;
  onBack: () => void;
  /** Small chip rendered next to the title (e.g. status). */
  badge?: React.ReactNode;
  /** Trailing slot pushed to the far right (e.g. kebab menu). */
  right?: React.ReactNode;
}) {
  return (
    <header className="cmd-builder-topbar">
      <button type="button" className="cmd-builder-back" onClick={onBack} aria-label="Назад">
        <ArrowLeftIcon />
      </button>
      <div className="cmd-builder-topbar-titlewrap">
        <span className="cmd-builder-topbar-title">{title}</span>
        {badge}
      </div>
      {right && <div className="cmd-builder-topbar-right">{right}</div>}
    </header>
  );
}

// ── Helpers ───────────────────────────────────────────────────────

function findAgent(
  catalogue: { agents?: Agent[]; defaultAgent?: Agent | null } | undefined,
  ref: string,
): Agent | null {
  const all = [
    ...(catalogue?.agents ?? []),
    ...(catalogue?.defaultAgent ? [catalogue.defaultAgent] : []),
  ];
  return all.find((a) => a.ref === ref) ?? null;
}

/** The agent that will actually run when the routine fires. Explicit pick
 *  wins; otherwise the catalogue's default agent (gigachat-business and
 *  friends) is the implicit fallback. Used for both the connectors-baseline
 *  computation and the agent_ref sent on save. */
function effectiveAgent(
  agentRef: string | null,
  catalogue: { agents?: Agent[]; defaultAgent?: Agent | null } | undefined,
): Agent | null {
  if (!agentRef) return catalogue?.defaultAgent ?? null;
  return findAgent(catalogue, agentRef);
}

function humanize(msg: string): string {
  const m = msg.match(/"detail":"([^"]+)"/);
  if (m && m[1]) return m[1];
  return msg.slice(0, 200);
}

function plural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "ов";
  if (mod10 === 1) return "";
  if (mod10 >= 2 && mod10 <= 4) return "а";
  return "ов";
}

// ── Icons ─────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <polyline points="3 5 6 8 9 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AtIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m4.5 4.5 7 7m0-7-7 7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

